import { t } from '@lingui/core/macro';
import { SupplierAndStockLocationDTO, SupplierDTO, SupplierPreference } from '@luminovo/api-contracts';
import { isPresent } from '@luminovo/commons';
import {
    colorSystem,
    CopyToClipboardTooltip,
    Dot,
    Flexbox,
    Link,
    StatusChip,
    StatusChipDefaultContainer,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { IconButton, styled } from '@mui/material';
import { CopyIcon } from 'lucide-react';
import type { JSX } from 'react';

import { formatRegionAsName, formatSupplierAndStockLocationDTO, formatSupplierDTO } from '../../formatters';

export function SupplierChip({
    isApproved,
    isPreferred,
    supplier,
}: {
    isApproved: boolean;
    isPreferred: boolean;
    supplier: SupplierDTO;
}): JSX.Element {
    const color = isPreferred || isApproved ? 'green' : 'red';

    return (
        <Tooltip
            variant="white"
            disableMaxWidth
            title={<SupplierChipTooltipContent supplier={supplier} isPreferred={isPreferred} isApproved={isApproved} />}
        >
            <span>
                <StatusChip
                    overrides={{ Container }}
                    color={color}
                    label={formatSupplierDTO(supplier)}
                    enableHoverEffect={true}
                />
            </span>
        </Tooltip>
    );
}

function SupplierChipTooltipContent({
    supplier,
    isPreferred,
    isApproved,
}: {
    supplier: SupplierDTO;
    isPreferred: boolean;
    isApproved: boolean;
}): JSX.Element {
    const statusColor = isPreferred ? 'primary' : isApproved ? 'green' : 'red';
    const statusLabel = isPreferred
        ? t`Preferred supplier`
        : isApproved
          ? t`Approved supplier`
          : t`Supplier not approved`;

    return (
        <Flexbox
            gap={8}
            style={{ padding: '8px' }}
            sx={{
                flexDirection: 'column',
            }}
        >
            <span onClick={(e) => e.stopPropagation()}>
                <Link
                    to="/suppliers"
                    attention="high"
                    variant="body-semibold"
                    style={{ color: colorSystem.neutral[9] }}
                >
                    {supplier.name}
                </Link>
            </span>
            {isPresent(supplier.supplier_number) && (
                <Flexbox
                    gap={6}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {supplier.supplier_number}
                    </Text>
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <CopyToClipboardTooltip value={supplier.supplier_number} variant="white">
                            <IconButton
                                aria-label={t`Copy to Clipboard`}
                                size="small"
                                sx={{
                                    padding: '2px',
                                    color: colorSystem.neutral[6],
                                    '&:hover': {
                                        color: colorSystem.neutral[8],
                                        backgroundColor: colorSystem.neutral[2],
                                    },
                                }}
                            >
                                <CopyIcon size={14} />
                            </IconButton>
                        </CopyToClipboardTooltip>
                    </span>
                </Flexbox>
            )}
            <StatusChip color={statusColor} label={statusLabel} />
        </Flexbox>
    );
}

export function SupplierAndStockLocationChip({
    isApproved,
    isPreferred,
    isExcluded,
    supplier,
}: {
    isApproved: boolean;
    isPreferred: boolean;
    isExcluded: boolean;
    supplier: SupplierAndStockLocationDTO;
}): JSX.Element {
    const color = () => {
        if (isPreferred) {
            return 'green';
        }
        if (isApproved) {
            return 'green';
        }
        if (isExcluded) {
            return 'red';
        }
        return 'red';
    };

    return (
        <Tooltip
            variant="white"
            disableMaxWidth
            title={
                <SupplierAndStockLocationChipTooltipContent
                    supplier={supplier}
                    isPreferred={isPreferred}
                    isApproved={isApproved}
                    isExcluded={isExcluded}
                />
            }
        >
            <span>
                <StatusChip
                    overrides={{ Container }}
                    color={color()}
                    label={formatSupplierAndStockLocationDTO(supplier)}
                    enableHoverEffect={true}
                />
            </span>
        </Tooltip>
    );
}

function SupplierAndStockLocationChipTooltipContent({
    supplier,
    isPreferred,
    isApproved,
    isExcluded,
}: {
    supplier: SupplierAndStockLocationDTO;
    isPreferred: boolean;
    isApproved: boolean;
    isExcluded: boolean;
}): JSX.Element {
    const supplierNumber = supplier.supplier_number ?? supplier.supplier.supplier_number ?? null;
    const statusColor = isPreferred ? 'primary' : isApproved ? 'green' : isExcluded ? 'red' : 'neutral';
    const statusLabel = isPreferred
        ? t`Preferred supplier`
        : isApproved
          ? t`Approved supplier`
          : isExcluded
            ? t`Excluded supplier`
            : t`Supplier not approved`;
    const locationName = isPresent(supplier.stock_location) ? formatRegionAsName(supplier.stock_location) : null;
    const shouldShowLocation = isPresent(locationName) && locationName.toLowerCase() !== 'unknown';

    return (
        <Flexbox
            gap={8}
            style={{ padding: '8px' }}
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                gap={8}
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <span onClick={(e) => e.stopPropagation()}>
                    <Link
                        to="/suppliers/$supplierAndStockLocationId"
                        params={{ supplierAndStockLocationId: supplier.id }}
                        attention="high"
                        variant="body-semibold"
                        style={{ color: colorSystem.neutral[9] }}
                    >
                        {supplier.supplier.name}
                    </Link>
                </span>
                {shouldShowLocation && (
                    <Text variant="body-small" color={colorSystem.neutral[6]}>
                        {locationName}
                    </Text>
                )}
            </Flexbox>
            {isPresent(supplierNumber) && (
                <Flexbox
                    gap={6}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {supplierNumber}
                    </Text>
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <CopyToClipboardTooltip value={supplierNumber} variant="white">
                            <IconButton
                                aria-label={t`Copy to Clipboard`}
                                size="small"
                                sx={{
                                    padding: '2px',
                                    color: colorSystem.neutral[6],
                                    '&:hover': {
                                        color: colorSystem.neutral[8],
                                        backgroundColor: colorSystem.neutral[2],
                                    },
                                }}
                            >
                                <CopyIcon size={14} />
                            </IconButton>
                        </CopyToClipboardTooltip>
                    </span>
                </Flexbox>
            )}
            <StatusChip color={statusColor} label={statusLabel} />
        </Flexbox>
    );
}

export function SupplierPreferenceLabel({ preference }: { preference: SupplierPreference }): JSX.Element {
    const preferenceState: Record<
        SupplierPreference,
        {
            label: string;
            color: string;
        }
    > = {
        [SupplierPreference.Preferred]: {
            label: t`Preferred`,
            color: colorSystem.primary[5],
        },
        [SupplierPreference.Approved]: {
            label: t`Approved`,
            color: colorSystem.green[5],
        },
        [SupplierPreference.NotApproved]: {
            label: t`Non-approved`,
            color: colorSystem.neutral[5],
        },
        [SupplierPreference.Excluded]: {
            label: t`Excluded`,
            color: colorSystem.red[5],
        },
    };

    return (
        <Flexbox
            gap={4}
            sx={{
                alignItems: 'center',
            }}
        >
            <Dot color={preferenceState[preference].color} />
            {preferenceState[preference].label}
        </Flexbox>
    );
}

const Container = styled(StatusChipDefaultContainer)({
    cursor: 'inherit',
    '& span': {
        maxWidth: 200,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
});
